import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    CpmBrandFilter,
    CpmButton,
    CpmFilterDropdown,
    CpmMasterTableSkeleton,
    CpmPageShell,
    CpmPagination,
    CpmPaginationLabel,
    CpmSearchSelect,
    CpmSelectionBar,
} from '../../../components/design-system';
import {
    CPM_CATALOG_VIEW,
    CPM_INDIAN_STATES,
    CPM_MASTER_AREA,
    CPM_MASTER_STATUS_FILTER_OPTIONS,
    formatCpmCount,
    formatVolumeLabel,
    masterListLevel,
    cpmLicenceExciseApproveBlocked,
} from '../../../constants/centralProductMaster.constants';
import { useAppDispatch } from '../../../store';
import { fetchStates, useCpmExciseCategory } from '../../../store/slices/cpmExciseCategorySlice';
import {
    beginListReload,
    clearBulkDrafts,
    clearFilters,
    clearSelection,
    enterBulkMode,
    exitBulkMode,
    fetchMasterList,
    patchFilters,
    setArea,
    setBulkDraftField,
    setCatalogView,
    expandAllRows,
    collapseAllRows,
    setPage,
    setChildSort,
    setPageSelection,
    toggleCollapsed,
    toggleSelectedItem,
    useCpmMasterEditor,
    type CpmSelectedMasterEntry,
} from '../../../store/slices/cpmMasterEditorSlice';
import type {
    CpmBrandRecord,
    CpmBulkUpsertItem,
    CpmCatalogView,
    CpmLicenseTypesByState,
    CpmMasterArea,
    CpmProductRecord,
    CpmStateProductRecord,
    CpmVariantRecord,
} from '../../../types/centralProductMaster.types';
import MergeDecision from '../MergeDecision/MergeDecision';
import ApproveDialog from './components/ApproveDialog';
import BulkConfirmDialog from './components/BulkConfirmDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import DiscardEditsWarnDialog from './components/DiscardEditsWarnDialog';
import MasterRecordTable from './components/MasterRecordTable';
import RecordFormDialog from './components/RecordFormDialog';

const IconEdit = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
    >
        <path d="M12 20h9" strokeLinecap="round" />
        <path
            d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconPlus = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
    >
        <path d="M5 12h14M12 5v14" strokeLinecap="round" />
    </svg>
);

const IconMerge = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
    >
        <path d="M8 3v6a4 4 0 0 0 4 4h8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3v6a4 4 0 0 1-4 4H4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m18 9 3 4-3 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconTrash = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
    >
        <path d="M3 6h18" strokeLinecap="round" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
);

const IconCheck = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
    >
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

type FormLevel = 'brand' | 'product' | 'variant' | 'stateProduct';

type FormEntry = {
    level: FormLevel;
    id: string | null;
    createProductId?: string | null;
    createProductName?: string | null;
};

function approveReasonFor(
    level: FormLevel,
    row:
        | CpmBrandRecord
        | CpmProductRecord
        | CpmVariantRecord
        | CpmStateProductRecord
        | CpmVariantNestedLike
): string {
    const status = String((row as { status?: string }).status ?? '');
    if (level === 'brand') {
        return status === 'NEEDS_REVIEW'
            ? 'Needs review because an automated check flagged one of its fields as low confidence and a curator has not confirmed the record yet.'
            : 'Pending because it was created by an ingestion batch and has not been confirmed by a curator yet.';
    }
    if (level === 'stateProduct') {
        const s = row as CpmStateProductRecord;
        if (!s.exciseCategory && !s.exciseCategoryName) {
            return 'Needs review because this state has no curated excise category for the variant — a curator has to map it before the filed prices can be trusted.';
        }
        return status === 'NEEDS_REVIEW'
            ? 'Needs review because the filed retail price moved outside the expected band for its excise category — a curator has to confirm it against the state notification it came from.'
            : 'Pending because the state pricing row arrived from an excise-notification batch and no curator has confirmed its category, license types or prices yet.';
    }
    if (level === 'product') {
        return status === 'NEEDS_REVIEW'
            ? 'Needs review because its name is a close match for another product under the same brand — a curator has to confirm it is a distinct product and not a duplicate.'
            : 'Pending because it was created by an ingestion batch and no curator has confirmed its alcohol content or brand link yet.';
    }
    return status === 'NEEDS_REVIEW'
        ? 'Needs review because a reported pack size or barcode did not agree with the other variants of this product — a curator has to confirm the pack details.'
        : 'Pending because it arrived from an ingestion batch and no curator has confirmed its volume, container or items-per-case yet.';
}

function pendingChildrenCountFor(
    level: FormLevel,
    row:
        | CpmBrandRecord
        | CpmProductRecord
        | CpmVariantRecord
        | CpmStateProductRecord
        | CpmVariantNestedLike
): number {
    if (level === 'product') {
        const p = row as CpmProductRecord;
        return (p.variants ?? []).filter(
            (v) => v.status === 'PENDING' || v.status === 'NEEDS_REVIEW'
        ).length;
    }
    // Brand product counts aren't nested in list payloads — omit kids callout unless we have data.
    return 0;
}

function approveFactsFor(
    level: FormLevel,
    row:
        | CpmBrandRecord
        | CpmProductRecord
        | CpmVariantRecord
        | CpmStateProductRecord
        | CpmVariantNestedLike
): Array<{ label: string; value: string }> {
    if (level === 'brand') {
        const b = row as CpmBrandRecord;
        return [
            { label: 'Country of origin', value: b.countryOfOrigin || '—' },
            { label: 'Parent company', value: b.parentCompany || '—' },
            {
                label: 'Linked products',
                value: b.productCount != null ? formatCpmCount(b.productCount) : '—',
            },
        ];
    }
    if (level === 'stateProduct') {
        const s = row as CpmStateProductRecord;
        return [
            { label: 'State', value: s.state },
            {
                label: 'Excise category',
                value: s.exciseCategoryName || 'Not curated',
            },
            {
                label: 'License types',
                value: (s.licenseType ?? []).join(', ') || '—',
            },
            {
                label: 'Retail price',
                value: s.retailPrice != null ? `₹${formatCpmCount(s.retailPrice)}` : '—',
            },
        ];
    }
    if (level === 'product') {
        const p = row as CpmProductRecord;
        return [
            { label: 'Brand', value: p.brandInfo?.name || '—' },
            {
                label: 'Variants',
                value: String(p.variants?.length ?? p.variantCount ?? 0),
            },
        ];
    }
    const v = row as CpmVariantRecord & CpmVariantNestedLike;
    const parent = [v.productInfo?.name, v.brandInfo?.name].filter(Boolean).join(' · ');
    const packVol = formatVolumeLabel(v.volumeMl, v.volumeUnit);
    return [
        { label: 'Product · Brand', value: parent || '—' },
        {
            label: 'Pack',
            value:
                [packVol !== '—' ? packVol : null, v.containerType].filter(Boolean).join(' · ') ||
                '—',
        },
        {
            label: 'Items / case',
            value: v.itemPerCase != null ? String(v.itemPerCase) : '—',
        },
    ];
}

type CpmVariantNestedLike = {
    _id: string;
    name: string;
    volumeMl?: number;
    volumeUnit?: string;
    containerType?: string;
    itemPerCase?: number;
    status: string;
};

type MergeableLevel = 'brand' | 'product' | 'variant';

/**
 * Classify selection for the action bar.
 * Combined view can select nested variants under different products — those
 * must open variant-level merge (not product merge).
 */
function selectionMeta(
    area: CpmMasterArea,
    catalogView: CpmCatalogView,
    selectedIds: string[],
    selectedItemsById: Record<string, CpmSelectedMasterEntry>
) {
    const tableLevel = masterListLevel(area, catalogView);
    const selected = selectedIds
        .map((id) => selectedItemsById[id]?.record)
        .filter((record): record is NonNullable<typeof record> => !!record);
    const count = selectedIds.length;
    const topLevelCount = selectedIds.filter(
        (id) => selectedItemsById[id] && !selectedItemsById[id].nested
    ).length;
    const nestedVariantCount = selectedIds.filter((id) => selectedItemsById[id]?.nested).length;

    let mergeLevel: MergeableLevel | null = null;
    if (count === 2 && tableLevel !== 'stateProduct') {
        if (topLevelCount === 2) {
            // Two top-level rows in brands / products / variants views.
            mergeLevel = tableLevel as MergeableLevel;
        } else if (nestedVariantCount === 2) {
            // Two nested variants under (possibly different) products in combined view.
            mergeLevel = 'variant';
        }
    }

    return {
        level: tableLevel,
        mergeLevel,
        count,
        canMerge: mergeLevel != null,
        canDelete: count === 1,
        selected,
        nestedVariantCount,
        topLevelCount,
    };
}

export default function MasterEditor() {
    const dispatch = useAppDispatch();
    const me = useCpmMasterEditor();
    const excise = useCpmExciseCategory();
    const [searchParams, setSearchParams] = useSearchParams();

    const [formStack, setFormStack] = useState<FormEntry[]>([]);
    const [approve, setApprove] = useState<{
        level: FormLevel;
        id: string;
        name: string;
        status: string;
        facts: Array<{ label: string; value: string }>;
        reason: string;
        pendingChildrenCount: number;
        categoryBlocked?: boolean;
        categoryBlockedText?: string;
    } | null>(null);
    const [del, setDel] = useState<{ level: string; id: string; name: string } | null>(null);
    const [merge, setMerge] = useState<{
        level: 'brand' | 'product' | 'variant';
        idA: string;
        idB: string;
    } | null>(null);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [discardWarn, setDiscardWarn] = useState(false);
    /** Local search text — API filter commits after 300ms debounce. */
    const [searchDraft, setSearchDraft] = useState(me.filters.query);

    const {
        area,
        catalogView,
        filters,
        items,
        pagination,
        loading,
        totals,
        listIntent,
        tableLabel,
        pageBusyLabel,
        searching,
    } = me;

    useEffect(() => {
        setSearchDraft(filters.query);
    }, [filters.query]);

    useEffect(() => {
        dispatch(fetchStates());
    }, [dispatch]);

    const licenseTypesByState = useMemo((): CpmLicenseTypesByState => {
        const map: CpmLicenseTypesByState = {};
        for (const s of excise.states) {
            map[s.name] = s.licenseTypes ?? [];
        }
        return map;
    }, [excise.states]);

    const licenseTypesReady =
        excise.statesLoading === 'success' ||
        excise.statesLoading === 'error' ||
        excise.states.length > 0;

    useEffect(() => {
        if (searchDraft === filters.query) return;
        const t = window.setTimeout(() => {
            dispatch(patchFilters({ query: searchDraft }));
        }, 300);
        return () => window.clearTimeout(t);
    }, [searchDraft, filters.query, dispatch]);

    useEffect(() => {
        const areaParam = searchParams.get('level');
        if (areaParam === 'brands' || areaParam === 'catalog' || areaParam === 'stateProducts') {
            if (areaParam !== area) dispatch(setArea(areaParam));
        }
        const viewParam = searchParams.get('view');
        if (viewParam === 'combined' || viewParam === 'products' || viewParam === 'variants') {
            if (viewParam !== catalogView) dispatch(setCatalogView(viewParam));
        }
        const statusParam = (searchParams.get('status') || '').toUpperCase();
        const brandIdParam = searchParams.get('brandId');
        const brandLabelParam = searchParams.get('brandLabel');
        const stateParam = searchParams.get('state');
        const exciseCategoryIdParam = searchParams.get('exciseCategoryId');
        const filterPatch: {
            status?: string;
            brandId?: string | null;
            brandLabel?: string | null;
            state?: string | null;
            exciseCategoryId?: string | null;
        } = {};
        if (
            ['ACTIVE', 'PENDING', 'NEEDS_REVIEW', 'MERGED'].includes(statusParam) &&
            statusParam !== filters.status
        ) {
            filterPatch.status = statusParam;
        }
        if (areaParam === 'catalog') {
            const nextBrandId = brandIdParam || null;
            const nextBrandLabel = nextBrandId ? brandLabelParam || null : null;
            if (nextBrandId !== filters.brandId) {
                filterPatch.brandId = nextBrandId;
                filterPatch.brandLabel = nextBrandLabel;
            } else if (nextBrandLabel !== filters.brandLabel) {
                filterPatch.brandLabel = nextBrandLabel;
            }
        }
        if (!statusParam && filters.status) {
            filterPatch.status = '';
        }
        if (stateParam && stateParam !== filters.state) {
            filterPatch.state = stateParam;
        }
        if (exciseCategoryIdParam && exciseCategoryIdParam !== filters.exciseCategoryId) {
            filterPatch.exciseCategoryId = exciseCategoryIdParam;
        }
        if (Object.keys(filterPatch).length > 0) {
            dispatch(patchFilters(filterPatch));
        }

        // Deep-link from merge success (VQ → Master Editor) or other callers.
        const openId = searchParams.get('openId');
        const openLevel = searchParams.get('openLevel');
        if (
            openId &&
            (openLevel === 'brand' ||
                openLevel === 'product' ||
                openLevel === 'variant' ||
                openLevel === 'stateProduct')
        ) {
            setFormStack([{ level: openLevel, id: openId }]);
            const p = new URLSearchParams(searchParams);
            p.delete('openId');
            p.delete('openLevel');
            setSearchParams(p, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        dispatch(fetchMasterList());
    }, [dispatch, area, catalogView, filters]);

    const dirtyKeys = Object.keys(me.bulkDrafts).filter(
        (k) => Object.keys(me.bulkDrafts[k] ?? {}).length > 0
    );
    const dirtyCount = dirtyKeys.length;

    const bulkItems: CpmBulkUpsertItem[] = useMemo(() => {
        return dirtyKeys.map((key) => {
            const [level, id] = key.split(':');
            return { level, id, fields: me.bulkDrafts[key] };
        });
    }, [dirtyKeys, me.bulkDrafts]);

    const stateOptions = useMemo(() => CPM_INDIAN_STATES.map((s) => ({ value: s, label: s })), []);

    const paginationNoun = useMemo(() => {
        const lvl = masterListLevel(area, catalogView);
        return {
            brand: 'brands',
            product: 'products',
            variant: 'variants',
            stateProduct: 'state records',
        }[lvl];
    }, [area, catalogView]);

    const nameByKey = useMemo(() => {
        const map: Record<string, string> = {};
        for (const row of items) {
            const lvl = masterListLevel(area, catalogView);
            map[`${lvl}:${row._id}`] = String((row as { name?: string }).name ?? row._id);
            if (area === 'catalog' && catalogView === 'combined') {
                for (const v of (row as CpmProductRecord).variants ?? []) {
                    map[`variant:${v._id}`] = v.name;
                }
            }
        }
        if (area === 'stateProducts') {
            for (const row of items as CpmStateProductRecord[]) {
                map[`stateProduct:${row._id}`] = row.variantInfo?.name ?? row.state;
            }
        }
        return map;
    }, [items, area, catalogView]);

    const meta = selectionMeta(area, catalogView, me.selectedIds, me.selectedItemsById);

    const headStatLabel =
        area === 'brands'
            ? 'Brands in catalog'
            : area === 'stateProducts'
              ? 'State Products in catalog'
              : catalogView === 'variants'
                ? 'Variants in catalog'
                : 'Products in catalog';

    const countText = (value?: number) => (value == null ? '—' : formatCpmCount(value));

    const activeAreaTotal =
        area === 'brands'
            ? totals.brand
            : area === 'stateProducts'
              ? totals.stateProduct
              : catalogView === 'variants'
                ? totals.variant
                : totals.product;

    const headStatValue = countText(activeAreaTotal);

    // Only the active tab carries a count — the other areas are never fetched.
    const activeAreaBadge =
        area === 'catalog'
            ? `${countText(totals.product)} · ${countText(totals.variant)}`
            : countText(activeAreaTotal);

    const matchTotal = pagination?.total ?? 0;
    const matchLabel = `${formatCpmCount(matchTotal)} ${
        matchTotal === 1
            ? area === 'brands'
                ? 'brand'
                : area === 'stateProducts'
                  ? 'state record'
                  : catalogView === 'variants'
                    ? 'variant'
                    : 'product'
            : area === 'brands'
              ? 'brands'
              : area === 'stateProducts'
                ? 'state records'
                : catalogView === 'variants'
                  ? 'variants'
                  : 'products'
    } match`;

    const filtersOn =
        !!filters.query ||
        !!filters.status ||
        !!filters.brandId ||
        !!filters.state ||
        !!filters.exciseCategoryId;

    const applyFilterSearchParams = (
        p: URLSearchParams,
        next: {
            status?: string;
            brandId?: string | null;
            brandLabel?: string | null;
        } = {}
    ) => {
        const status = next.status !== undefined ? next.status : filters.status;
        const brandId = next.brandId !== undefined ? next.brandId : filters.brandId;
        const brandLabel = next.brandLabel !== undefined ? next.brandLabel : filters.brandLabel;
        if (status) p.set('status', status);
        else p.delete('status');
        if (brandId) p.set('brandId', brandId);
        else p.delete('brandId');
        if (brandLabel) p.set('brandLabel', brandLabel);
        else p.delete('brandLabel');
    };

    const selectArea = (next: CpmMasterArea) => {
        if (me.bulkMode && dirtyCount > 0) {
            setDiscardWarn(true);
            return;
        }
        dispatch(setArea(next));
        const p = new URLSearchParams(searchParams);
        p.set('level', next);
        applyFilterSearchParams(p, next === 'catalog' ? {} : { brandId: null, brandLabel: null });
        setSearchParams(p, { replace: true });
    };

    const selectView = (next: CpmCatalogView) => {
        if (me.bulkMode && dirtyCount > 0) {
            setDiscardWarn(true);
            return;
        }
        dispatch(setCatalogView(next));
        const p = new URLSearchParams(searchParams);
        p.set('view', next);
        setSearchParams(p, { replace: true });
    };

    const goToBrandProducts = (brandId: string, brandName: string) => {
        if (me.bulkMode && dirtyCount > 0) {
            setDiscardWarn(true);
            return;
        }
        dispatch(setArea('catalog'));
        dispatch(setCatalogView('combined'));
        dispatch(patchFilters({ brandId, brandLabel: brandName }));
        const p = new URLSearchParams(searchParams);
        p.set('level', 'catalog');
        p.set('view', 'combined');
        applyFilterSearchParams(p, { brandId, brandLabel: brandName });
        setSearchParams(p);
    };

    const requestExitBulk = () => {
        if (dirtyCount > 0) setDiscardWarn(true);
        else dispatch(exitBulkMode());
    };

    const refresh = () => {
        dispatch(beginListReload({ intent: 'replace', label: 'Reloading records' }));
        dispatch(fetchMasterList());
        dispatch(clearSelection());
        dispatch(clearBulkDrafts());
    };

    // Cold load / emptied replace must always skeleton — URL filter sync can set
    // listIntent to 'page' before the first rows arrive.
    const tableReplaceLoading =
        loading === 'loading' && (listIntent === 'replace' || items.length === 0);
    const tableBusyPage = loading === 'loading' && listIntent === 'page' && items.length > 0;

    const openForm = (
        level: FormLevel,
        id: string | null,
        createProductId?: string | null,
        createProductName?: string | null
    ) => {
        setFormStack([{ level, id, createProductId, createProductName }]);
    };

    const pushForm = (entry: FormEntry) => {
        setFormStack((prev) => [...prev, entry]);
    };

    const popForm = () => {
        setFormStack((prev) => prev.slice(0, -1));
    };

    const clearFormStack = () => {
        setFormStack([]);
    };

    const selectedName = (() => {
        if (me.selectedIds.length !== 1) return 'record';
        const cached = me.selectedItemsById[me.selectedIds[0]];
        if (!cached) return 'record';
        if (area === 'stateProducts') {
            return (cached.record as CpmStateProductRecord).variantInfo?.name ?? 'state record';
        }
        return String((cached.record as { name?: string }).name ?? 'record');
    })();

    const selectedLevelForDelete = (() => {
        if (me.selectedIds.length !== 1) return meta.level;
        const cached = me.selectedItemsById[me.selectedIds[0]];
        if (cached?.nested) return 'variant';
        return meta.level;
    })();

    const tabButtonClass = (active: boolean) =>
        [
            'rounded-t-[var(--cpm-radius-10)] cursor-pointer flex items-center border-b-2 -mb-px transition-colors',
            active
                ? 'text-[var(--cpm-brand-navy)] bg-white border-[var(--cpm-brand-purple)]'
                : 'text-[var(--cpm-text-secondary)] border-transparent hover:text-[var(--cpm-brand-navy)]',
        ].join(' ');

    const viewPillClass = (active: boolean) =>
        [
            'inline-flex items-center rounded-[8px] transition-colors cursor-pointer',
            active
                ? 'bg-white text-[var(--cpm-brand-navy)] shadow-[0_1px_2px_rgba(14,8,88,.08),0_6px_16px_-8px_rgba(14,8,88,.24)]'
                : 'text-[var(--cpm-text-secondary)]',
        ].join(' ');

    const searchPlaceholder =
        area === 'stateProducts'
            ? 'Search by variant or state'
            : area === 'brands'
              ? 'Search brands by name'
              : catalogView === 'combined'
                ? 'Search products or their variants'
                : catalogView === 'variants'
                  ? 'Search variants by name'
                  : 'Search products by name';

    const resolveApproveRow = (level: FormLevel, id: string) => {
        if (level === 'variant' && catalogView === 'combined') {
            for (const row of items as CpmProductRecord[]) {
                const nested = row.variants?.find((x) => x._id === id);
                if (nested) {
                    return {
                        ...nested,
                        productInfo: { name: row.name },
                        brandInfo: row.brandInfo,
                    };
                }
            }
        }
        return items.find((i) => i._id === id) ?? null;
    };

    return (
        <CpmPageShell gap={20} maxWidth={1420} variant="masterEditor">
            <div
                className="flex items-center text-[var(--cpm-text-secondary)]"
                style={{ gap: 8, fontSize: 12.5, fontWeight: 600 }}
            >
                <span>Admin</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span>Catalog</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <Link
                    to="/central-product-master"
                    className="!text-[var(--cpm-text-secondary)] hover:!text-[var(--cpm-brand-navy)] !font-semibold"
                >
                    Central Product Master
                </Link>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                    Master Editor
                </span>
            </div>

            <div className="flex items-end justify-between flex-wrap" style={{ gap: 32 }}>
                <div className="flex flex-col max-w-[700px]" style={{ gap: 6 }}>
                    <div
                        className="uppercase text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                    >
                        Central Product Master
                    </div>
                    <h1 className="m-0 !text-[30px] !font-extrabold !tracking-[-0.025em] !leading-[1.05] !text-[var(--cpm-brand-navy)]">
                        Master Editor
                    </h1>
                    <p className="m-0 !text-[14px] !font-medium !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        Browse the canonical catalog and correct it directly. Open a record to edit
                        its fields, or select rows to merge and delete.
                    </p>
                </div>
                <div className="flex flex-col" style={{ gap: 4 }}>
                    <span
                        className="uppercase text-[var(--cpm-text-tertiary)]"
                        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                    >
                        {headStatLabel}
                    </span>
                    <span
                        className="tabular text-[var(--cpm-brand-navy)]"
                        style={{
                            fontSize: 30,
                            fontWeight: 800,
                            letterSpacing: '-0.025em',
                            lineHeight: 1,
                        }}
                    >
                        {headStatValue}
                    </span>
                </div>
            </div>

            <div className="flex items-end gap-[4px] border-b border-[var(--cpm-border-default)]">
                {(
                    [
                        { key: CPM_MASTER_AREA.brands, label: 'Brands' },
                        {
                            key: CPM_MASTER_AREA.catalog,
                            label: 'Products & Variants',
                        },
                        {
                            key: CPM_MASTER_AREA.stateProducts,
                            label: 'State Products',
                        },
                    ] as const
                ).map((tab) => {
                    const active = area === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => selectArea(tab.key)}
                            className={tabButtonClass(active)}
                            style={{
                                padding: '11px 16px 12px',
                                gap: 8,
                                fontSize: 13.5,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            <span>{tab.label}</span>
                            {active ? (
                                <span
                                    className="rounded-full tabular bg-[var(--cpm-brand-navy)] text-white"
                                    style={{ padding: '2px 9px', fontSize: 11, fontWeight: 800 }}
                                >
                                    {activeAreaBadge}
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {area === 'catalog' ? (
                <div className="flex items-center flex-wrap" style={{ gap: 14 }}>
                    <span
                        className="uppercase text-[var(--cpm-text-tertiary)]"
                        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                    >
                        View
                    </span>
                    <div
                        className="inline-flex items-center rounded-[11px] bg-[var(--cpm-surface-skeleton)]"
                        style={{ gap: 5, padding: 4 }}
                    >
                        {(
                            [
                                {
                                    key: CPM_CATALOG_VIEW.combined,
                                    label: 'Products with variants',
                                },
                                {
                                    key: CPM_CATALOG_VIEW.products,
                                    label: 'Products only',
                                },
                                {
                                    key: CPM_CATALOG_VIEW.variants,
                                    label: 'Variants only',
                                },
                            ] as const
                        ).map((v) => (
                            <button
                                key={v.key}
                                type="button"
                                onClick={() => selectView(v.key)}
                                className={viewPillClass(catalogView === v.key)}
                                style={{
                                    padding: '8px 14px',
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12, fontWeight: 600 }}
                    >
                        {catalogView === 'combined'
                            ? 'Each product with the variants that belong to it. Open either level to edit it.'
                            : catalogView === 'products'
                              ? 'One row per product — variants are hidden in this view.'
                              : 'One row per variant, flat across every product.'}
                    </span>
                    {catalogView === 'combined' && !me.bulkMode ? (
                        <button
                            type="button"
                            onClick={() => {
                                const anyCollapsed = (items as CpmProductRecord[]).some((r) =>
                                    me.collapsedIds.includes(r._id)
                                );
                                if (anyCollapsed) dispatch(expandAllRows());
                                else
                                    dispatch(
                                        collapseAllRows(
                                            (items as CpmProductRecord[]).map((r) => r._id)
                                        )
                                    );
                            }}
                            className="ml-auto inline-flex items-center gap-[7px] h-[32px] px-[13px] rounded-[9px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[12px] font-extrabold text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path
                                    d="m7 15 5 5 5-5M7 9l5-5 5 5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            {(items as CpmProductRecord[]).some((r) =>
                                me.collapsedIds.includes(r._id)
                            )
                                ? 'Expand all'
                                : 'Collapse all'}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {me.bulkMode ? (
                <div
                    className="flex items-center flex-wrap rounded-[12px] bg-[var(--cpm-purple-tint-13)] border-[1.5px] border-[var(--cpm-brand-purple)]"
                    style={{ gap: 14, padding: '12px 16px' }}
                >
                    <span className="inline-flex items-center gap-[7px] text-[13px] font-extrabold tracking-[-0.01em] text-[var(--cpm-brand-navy)]">
                        <IconEdit />
                        Inline edit mode
                    </span>
                    <span
                        className={[
                            'inline-flex items-center px-[10px] py-[3px] rounded-full text-[11.5px] font-extrabold',
                            dirtyCount > 0
                                ? 'bg-[var(--cpm-brand-purple)] text-white'
                                : 'bg-white text-[var(--cpm-text-secondary)]',
                        ].join(' ')}
                    >
                        {dirtyCount} uncommitted {dirtyCount === 1 ? 'change' : 'changes'}
                    </span>
                    <span className="text-[11.5px] font-bold text-[var(--cpm-brand-purple)]">
                        Purple fields hold uncommitted edits. Selection and per-row Edit are paused.
                    </span>
                    <div className="ml-auto flex items-center gap-[10px]">
                        <CpmButton variant="outline" size="sm" onClick={requestExitBulk}>
                            Cancel
                        </CpmButton>
                        <CpmButton
                            variant="navy"
                            size="sm"
                            leadingIcon={<IconCheck />}
                            disabled={dirtyCount === 0}
                            onClick={() => setBulkConfirmOpen(true)}
                        >
                            Save all changes
                        </CpmButton>
                    </div>
                </div>
            ) : null}

            <div
                className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex items-end flex-wrap"
                style={{ padding: '14px 16px', gap: 12 }}
            >
                <div className="flex flex-col flex-1 min-w-[260px]" style={{ gap: 6 }}>
                    <span
                        className="uppercase text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
                    >
                        Search by name
                    </span>
                    <div
                        className={[
                            'flex items-center rounded-[10px] border-[1.5px] bg-white',
                            searchDraft
                                ? 'border-[var(--cpm-brand-purple)] shadow-[var(--cpm-ring-focus)]'
                                : 'border-[var(--cpm-border-strong)]',
                        ].join(' ')}
                        style={{ height: 38, paddingLeft: 12, paddingRight: 12, gap: 8 }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2"
                            className="shrink-0"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" strokeLinecap="round" />
                        </svg>
                        <input
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[13px] font-bold text-[var(--cpm-text-primary)] placeholder:text-[var(--cpm-text-secondary)] placeholder:font-semibold"
                        />
                        {searching ? (
                            <span
                                className="cpm-spin"
                                style={{ width: 14, height: 14, borderWidth: 2 }}
                            />
                        ) : null}
                        {searchDraft && !searching ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchDraft('');
                                    dispatch(patchFilters({ query: '' }));
                                }}
                                className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]"
                            >
                                <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                >
                                    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                                </svg>
                            </button>
                        ) : null}
                    </div>
                </div>

                <CpmFilterDropdown
                    label="Status"
                    options={CPM_MASTER_STATUS_FILTER_OPTIONS.filter((o) => o.value !== '').map(
                        (o) => ({ value: o.value, label: o.label })
                    )}
                    selected={filters.status ? [filters.status] : []}
                    multi={false}
                    emptyLabel="All statuses"
                    onChange={(vals) => {
                        const status = vals[0] ?? '';
                        dispatch(patchFilters({ status }));
                        const p = new URLSearchParams(searchParams);
                        applyFilterSearchParams(p, { status });
                        setSearchParams(p, { replace: true });
                    }}
                    className="!flex-none"
                    minWidth={172}
                />

                {area === 'catalog' ? (
                    <CpmBrandFilter
                        valueId={filters.brandId}
                        valueLabel={filters.brandLabel}
                        onChange={({ id, label }) => {
                            dispatch(patchFilters({ brandId: id, brandLabel: label }));
                            const p = new URLSearchParams(searchParams);
                            applyFilterSearchParams(p, { brandId: id, brandLabel: label });
                            setSearchParams(p, { replace: true });
                        }}
                    />
                ) : null}

                {area === 'stateProducts' ? (
                    <CpmSearchSelect
                        label="State"
                        placeholder="Search state"
                        options={stateOptions}
                        value={filters.state}
                        onChange={(next) =>
                            dispatch(
                                patchFilters({
                                    state: next,
                                    exciseCategoryId: null,
                                })
                            )
                        }
                        className="!flex-none"
                        minWidth={200}
                    />
                ) : null}

                {filtersOn ? (
                    <button
                        type="button"
                        onClick={() => {
                            dispatch(clearFilters());
                            const p = new URLSearchParams(searchParams);
                            applyFilterSearchParams(p, {
                                status: '',
                                brandId: null,
                                brandLabel: null,
                            });
                            setSearchParams(p, { replace: true });
                        }}
                        className="inline-flex items-center gap-[7px] h-[38px] px-[14px] rounded-[10px] border-[1.5px] border-[var(--cpm-brand-purple)] text-[12.5px] font-extrabold text-[var(--cpm-brand-purple)] bg-white hover:bg-[var(--cpm-purple-tint-13)]"
                    >
                        Clear filters
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                    </button>
                ) : null}

                <span
                    className="ml-auto text-[var(--cpm-text-secondary)] tabular whitespace-nowrap"
                    style={{
                        height: 38,
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: 12.5,
                        fontWeight: 700,
                    }}
                >
                    {matchLabel}
                </span>

                {!me.bulkMode ? (
                    <CpmButton
                        variant="outline"
                        size="sm"
                        leadingIcon={<IconEdit />}
                        onClick={() => dispatch(enterBulkMode())}
                        style={{ height: 38 }}
                    >
                        Edit rows inline
                    </CpmButton>
                ) : null}

                {area === 'brands' && !me.bulkMode ? (
                    <CpmButton
                        variant="navy"
                        size="sm"
                        leadingIcon={<IconPlus />}
                        onClick={() => openForm('brand', null)}
                        style={{ height: 38 }}
                    >
                        New brand
                    </CpmButton>
                ) : null}

                {area === 'catalog' &&
                (catalogView === 'variants' || catalogView === 'combined') &&
                !me.bulkMode ? (
                    <div className="flex items-center gap-[10px]">
                        <Link to="/central-product-master/volume-settings">
                            <CpmButton
                                variant="outline"
                                size="sm"
                                style={{ height: 38 }}
                                leadingIcon={
                                    <svg
                                        width="13"
                                        height="13"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <path
                                            d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                }
                            >
                                Volume settings
                            </CpmButton>
                        </Link>
                        <CpmButton
                            variant="navy"
                            size="sm"
                            leadingIcon={<IconPlus />}
                            onClick={() => openForm('variant', null)}
                            style={{ height: 38 }}
                        >
                            New variant
                        </CpmButton>
                    </div>
                ) : null}
            </div>

            <CpmSelectionBar
                count={me.selectedIds.length}
                noun={
                    area === 'brands'
                        ? 'brands'
                        : area === 'stateProducts'
                          ? 'state records'
                          : catalogView === 'variants'
                            ? 'variants'
                            : catalogView === 'products'
                              ? 'products'
                              : meta.mergeLevel === 'variant'
                                ? 'variants'
                                : meta.mergeLevel === 'product'
                                  ? 'products'
                                  : 'records'
                }
                onClear={() => dispatch(clearSelection())}
                helperText={
                    area === 'stateProducts'
                        ? me.selectedIds.length === 1
                            ? 'Delete removes this state pricing row only — state rows are not merged.'
                            : 'State pricing rows are not merged. Select exactly one row to delete it.'
                        : me.selectedIds.length === 1
                          ? 'Select one more record at this level to merge.'
                          : meta.canMerge
                            ? meta.mergeLevel === 'variant' && meta.nestedVariantCount === 2
                                ? 'Two variants selected — merge is available.'
                                : 'Two records selected — merge is available.'
                            : me.selectedIds.length === 2
                              ? 'Merge needs two records of the same level (two products, or two variants).'
                              : 'Merge needs exactly two records; delete needs exactly one.'
                }
            >
                {meta.canMerge && meta.mergeLevel ? (
                    <CpmButton
                        variant="selectionGhost"
                        size="selection"
                        leadingIcon={<IconMerge />}
                        onClick={() =>
                            setMerge({
                                level: meta.mergeLevel!,
                                idA: me.selectedIds[0],
                                idB: me.selectedIds[1],
                            })
                        }
                    >
                        Merge
                    </CpmButton>
                ) : null}
                {meta.canDelete ? (
                    <CpmButton
                        variant="selectionGhost"
                        size="selection"
                        leadingIcon={<IconTrash />}
                        onClick={() =>
                            setDel({
                                level: selectedLevelForDelete,
                                id: me.selectedIds[0],
                                name: selectedName,
                            })
                        }
                    >
                        Delete
                    </CpmButton>
                ) : null}
            </CpmSelectionBar>

            <div className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] overflow-hidden">
                <div
                    className="overflow-auto transition-opacity duration-150"
                    style={{
                        maxHeight: 'calc(100vh - 132px)',
                        opacity: tableBusyPage ? 0.45 : 1,
                        pointerEvents: tableBusyPage ? 'none' : undefined,
                    }}
                >
                    {tableReplaceLoading ? (
                        <CpmMasterTableSkeleton label={tableLabel} />
                    ) : loading === 'error' ? (
                        <div
                            className="flex flex-col items-center text-center"
                            style={{ gap: 12, padding: '56px 20px 52px' }}
                        >
                            <span
                                className="inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                                style={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 14,
                                    background: 'var(--cpm-shortfall-bg)',
                                    border: '1px solid var(--cpm-shortfall-border)',
                                }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                            </span>
                            <span className="!text-[17px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                                Couldn't load the catalog
                            </span>
                            <span className="max-w-[380px] !text-[12.5px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                The catalog service did not respond. Nothing was changed — your
                                filters are still applied.
                            </span>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                style={{ marginTop: 4 }}
                                leadingIcon={
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                }
                                onClick={refresh}
                            >
                                Try again
                            </CpmButton>
                        </div>
                    ) : (
                        <MasterRecordTable
                            area={area}
                            catalogView={catalogView}
                            items={items}
                            selectedIds={me.selectedIds}
                            collapsedIds={me.collapsedIds}
                            bulkMode={me.bulkMode}
                            bulkDrafts={me.bulkDrafts}
                            sortBy={filters.sortBy}
                            sortDir={filters.sortDir}
                            childSortKey={me.childSortKey}
                            childSortDir={me.childSortDir}
                            licenseTypesByState={licenseTypesByState}
                            licenseTypesReady={licenseTypesReady}
                            onSort={(key) => {
                                const same = filters.sortBy === key;
                                dispatch(
                                    patchFilters({
                                        sortBy: key,
                                        sortDir: same
                                            ? filters.sortDir === 'asc'
                                                ? 'desc'
                                                : 'asc'
                                            : 'asc',
                                    })
                                );
                            }}
                            onChildSort={(key) => dispatch(setChildSort({ key }))}
                            onToggle={(record, nested) =>
                                dispatch(toggleSelectedItem({ record, nested }))
                            }
                            onToggleAll={(checked) =>
                                dispatch(setPageSelection({ records: items, checked }))
                            }
                            onToggleExpand={(id) => dispatch(toggleCollapsed(id))}
                            onOpen={(level, id) => openForm(level as FormLevel, id)}
                            onViewProducts={goToBrandProducts}
                            onApprove={(level, id, name, status) => {
                                const formLevel = level as FormLevel;
                                const row = resolveApproveRow(formLevel, id);
                                const stateProduct =
                                    formLevel === 'stateProduct'
                                        ? (row as CpmStateProductRecord | null)
                                        : null;
                                const categoryBlocked = !!(
                                    stateProduct &&
                                    !stateProduct.exciseCategory &&
                                    !stateProduct.exciseCategoryName
                                );
                                setApprove({
                                    level: formLevel,
                                    id,
                                    name,
                                    status,
                                    facts: row
                                        ? approveFactsFor(formLevel, row as CpmBrandRecord)
                                        : [],
                                    reason: row
                                        ? approveReasonFor(formLevel, row as CpmBrandRecord)
                                        : '',
                                    pendingChildrenCount: row
                                        ? pendingChildrenCountFor(formLevel, row as CpmBrandRecord)
                                        : 0,
                                    categoryBlocked,
                                    categoryBlockedText: categoryBlocked
                                        ? cpmLicenceExciseApproveBlocked(
                                              stateProduct?.state || 'this state'
                                          )
                                        : undefined,
                                });
                            }}
                            onCreateVariant={(productId, productName) =>
                                openForm('variant', null, productId, productName)
                            }
                            onBulkField={(key, field, value) =>
                                dispatch(setBulkDraftField({ key, field, value }))
                            }
                        />
                    )}
                </div>

                {pagination && !tableReplaceLoading ? (
                    <div
                        className="flex items-center flex-wrap gap-[16px] border-t border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] rounded-b-[16px]"
                        style={{ padding: '12px 18px' }}
                    >
                        <CpmPaginationLabel
                            page={pagination.page}
                            limit={pagination.limit}
                            total={pagination.total}
                            noun={paginationNoun}
                        />
                        {tableBusyPage && pageBusyLabel ? (
                            <span className="inline-flex items-center" style={{ gap: 8 }}>
                                <span
                                    className="cpm-spin"
                                    style={{ width: 13, height: 13, borderWidth: 2 }}
                                />
                                <span
                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    {pageBusyLabel}
                                </span>
                            </span>
                        ) : null}
                        <div className="flex items-center gap-[8px]">
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
                            >
                                Rows
                            </span>
                            <CpmFilterDropdown
                                label=""
                                options={[
                                    { value: '25', label: '25' },
                                    { value: '50', label: '50' },
                                    { value: '100', label: '100' },
                                ]}
                                selected={[String(filters.limit)]}
                                multi={false}
                                includeEmptyOption={false}
                                emptyLabel={String(filters.limit)}
                                triggerLabel={String(filters.limit)}
                                highlightSelected={false}
                                onChange={(vals) => {
                                    const n = parseInt(vals[0] || '25', 10);
                                    dispatch(patchFilters({ limit: n, page: 1 }));
                                }}
                                className="!flex-none !min-w-[72px] !max-w-[88px]"
                                minWidth={72}
                                openUp
                            />
                        </div>
                        {dirtyCount > 0 && me.bulkMode ? (
                            <span className="inline-flex items-center gap-[8px] px-[12px] py-[5px] rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)] text-[11.5px] font-extrabold">
                                {dirtyCount} dirty {dirtyCount === 1 ? 'row' : 'rows'}
                            </span>
                        ) : null}
                        <div className="ml-auto [&_>div]:border-t-0 [&_>div]:bg-transparent [&_>div]:rounded-none [&_>div]:p-0">
                            <CpmPagination
                                page={pagination.page}
                                limit={pagination.limit}
                                total={pagination.total}
                                onPageChange={(page) => dispatch(setPage(page))}
                                hideLabel
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {formStack.map((entry, index) => {
                const isTop = index === formStack.length - 1;
                return (
                    <RecordFormDialog
                        key={`${entry.level}:${entry.id ?? 'new'}:${index}`}
                        open
                        level={entry.level}
                        id={entry.id}
                        createProductId={entry.createProductId}
                        createProductName={entry.createProductName}
                        zIndex={60 + index * 4}
                        isTop={isTop}
                        licenseTypesByState={licenseTypesByState}
                        licenseTypesReady={licenseTypesReady}
                        onClose={popForm}
                        onSaved={refresh}
                        onRequestDelete={(level, id, name) => {
                            clearFormStack();
                            setDel({ level, id, name });
                        }}
                        onOpenVariant={
                            entry.level === 'product'
                                ? (variantId) => {
                                      pushForm({ level: 'variant', id: variantId });
                                  }
                                : undefined
                        }
                    />
                );
            })}

            {approve ? (
                <ApproveDialog
                    open
                    level={approve.level}
                    masterId={approve.id}
                    name={approve.name}
                    status={approve.status}
                    facts={approve.facts}
                    reason={approve.reason}
                    pendingChildrenCount={approve.pendingChildrenCount}
                    categoryBlocked={approve.categoryBlocked}
                    categoryBlockedText={approve.categoryBlockedText}
                    onAssignCategory={
                        approve.categoryBlocked
                            ? () => {
                                  const id = approve.id;
                                  setApprove(null);
                                  openForm('stateProduct', id);
                              }
                            : undefined
                    }
                    onClose={() => setApprove(null)}
                    onApproved={refresh}
                />
            ) : null}

            {del ? (
                <DeleteConfirmDialog
                    open
                    level={del.level}
                    id={del.id}
                    name={del.name}
                    onClose={() => setDel(null)}
                    onDeleted={refresh}
                />
            ) : null}

            {bulkConfirmOpen ? (
                <BulkConfirmDialog
                    open
                    items={bulkItems}
                    nameByKey={nameByKey}
                    onClose={() => setBulkConfirmOpen(false)}
                    onCommitted={() => {
                        dispatch(exitBulkMode());
                        refresh();
                    }}
                />
            ) : null}

            <DiscardEditsWarnDialog
                open={discardWarn}
                dirtyCount={dirtyCount}
                onKeep={() => setDiscardWarn(false)}
                onDiscard={() => {
                    setDiscardWarn(false);
                    dispatch(exitBulkMode());
                }}
            />

            {merge ? (
                <MergeDecision
                    open
                    level={merge.level}
                    idA={merge.idA}
                    idB={merge.idB}
                    backLabel="Master Editor"
                    onClose={() => setMerge(null)}
                    onMerged={refresh}
                    onOpenSurvivor={(level, id) => {
                        setMerge(null);
                        openForm(level, id);
                    }}
                />
            ) : null}
        </CpmPageShell>
    );
}
